import React, { useEffect, useRef, useState } from 'react';
import { t } from '../i18n'; // Import auto-translation function
import './About.css';
import heroVideo from '../images/hero photos/0606.mp4'; // Import the hero video
import { Link } from 'react-router-dom'; // Import Link component
import { MdPerson, MdWoman } from 'react-icons/md';
import { FaLightbulb, FaUserTie, FaHandsHelping, FaLock } from 'react-icons/fa';

// Custom hook for scroll animations
function useFadeInOnScroll() {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 100) {
        el.classList.add('visible');
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return ref;
}

const About = ({ language = 'bg' }) => {
  const heroRef = useFadeInOnScroll();
  const missionRef = useFadeInOnScroll();
  const valuesRef = useFadeInOnScroll();
  const testimonialsRef = useFadeInOnScroll();
  const ctaRef = useFadeInOnScroll();

  // All content in Bulgarian - will be auto-translated
  const bgContent = {
    hero: {
      title: 'За нас'
    },
    mission: {
      title: 'Нашата мисия',
      content: 'Вдъхновяваме хората да се грижат за себе си и своето здраве чрез най-иновативните решения в регенеративната медицина. Стремим се да бъдем доверен партньор на всеки, който търси естествено възстановяване и дълготрайни резултати.',
      visionTitle: 'Визия',
      visionContent: 'Мечтаем за свят, в който науката и човечността вървят ръка за ръка. Вярваме, че с правилния подход и грижа, всеки може да отключи своя потенциал за обновление и хармония.'
    },
    values: {
      title: 'Нашите ценности',
      items: [
        {
          icon: '💡',
          label: 'Иновации',
          text: 'Винаги търсим нови решения и не се страхуваме да експериментираме.'
        },
        {
          icon: '🎓',
          label: 'Професионализъм',
          text: 'Работим с уважение, отговорност и отдаденост към всеки клиент.'
        },
        {
          icon: '🤝',
          label: 'Грижа',
          text: 'За нас здравето и доверието са най-ценните ресурси.'
        },
        {
          icon: '🔒',
          label: 'Доверие',
          text: 'Изграждаме дългосрочни отношения, основани на честност и прозрачност.'
        }
      ]
    },
    testimonials: {
      title: 'Какво казват нашите клиенти',
      items: [
        {
          name: 'Мария Петрова',
          role: 'Клиент',
          feedback_bg: 'Изключително доволна съм от услугите! Екипът е професионален и внимателен.',
          feedback_en: 'I am extremely satisfied with the services! The team is professional and attentive.',
          gender: 'woman',
        },
        {
          name: 'Димитър Иванов',
          role: 'Клиент',
          feedback_bg: 'Видях реални резултати и ще препоръчам на приятели.',
          feedback_en: 'I saw real results and will recommend to friends.',
          gender: 'man',
        },
        {
          name: 'Елена Стойкова',
          role: 'Клиент',
          feedback_bg: 'Много съм впечатлена от индивидуалния подход и грижата.',
          feedback_en: 'I am very impressed by the individual approach and care.',
          gender: 'woman',
        },
        {
          name: 'Георги Петров',
          role: 'Клиент',
          feedback_bg: 'Бързо обслужване и отлични резултати.',
          feedback_en: 'Fast service and excellent results.',
          gender: 'man',
        },
        {
          name: 'Ива Николова',
          role: 'Клиент',
          feedback_bg: 'Чувствам се много по-добре след терапията. Препоръчвам с две ръце!',
          feedback_en: 'I feel much better after the therapy. Highly recommend!',
          gender: 'woman',
        },
        {
          name: 'Петър Димов',
          role: 'Клиент',
          feedback_bg: 'Отлично отношение и професионализъм. Ще се върна отново.',
          feedback_en: 'Excellent attitude and professionalism. I will come back again.',
          gender: 'man',
        },
      ]
    },
    cta: {
      title: 'Готови ли сте да започнем заедно?',
      description: 'Свържете се с нас и нека заедно открием най-доброто решение за вашето здраве и самочувствие.',
      button: 'Свържете се с нас'
    }
  };

  const [selectedReview, setSelectedReview] = useState(0);
  const testimonials = bgContent.testimonials.items;

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero" ref={heroRef}>
        <video 
          className="hero-video-background"
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="about-hero-background"></div>
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <h1 className="white-bold-title">{t(bgContent.hero.title, language)}</h1>
        </div>
        <button onClick={() => document.querySelector('.about-mission').scrollIntoView({ behavior: 'smooth' })} className="home-scroll-btn" aria-label="Scroll to next section">
          <span className="down-arrow">&#10095;</span>
        </button>
      </section>

      {/* Mission & Vision Section */}
      <section className="about-mission" ref={missionRef} style={{ background: '#f8f9fa' }}>
        <div className="about-container">
          <div className="mission-layout">
            <div className="mission-content" style={{textAlign: 'left'}}>
              <h3 className="section-label">{t('МИСИЯ', language)}</h3>
              <h2>{t(bgContent.mission.title, language)}</h2>
              <p>{t(bgContent.mission.content, language)}</p>
            </div>
            <div className="mission-content" style={{textAlign: 'left'}}>
              <h3 className="section-label">{t('ВИЗИЯ', language)}</h3>
              <h2>{t(bgContent.mission.visionTitle, language)}</h2>
              <p>{t(bgContent.mission.visionContent, language)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values" ref={valuesRef}>
        <div className="about-container">
          <h3 className="section-label">{t('ЦЕННОСТИ', language)}</h3>
          <h2>{t(bgContent.values.title, language)}</h2>
          <div className="values-grid">
            <div className="value-card" style={{ animationDelay: `0s` }}>
              <div className="value-icon-svg"><FaLightbulb /></div>
              <h3>{t('Иновации', language)}</h3>
              <p>{t('Винаги търсим нови решения и не се страхуваме да експериментираме.', language)}</p>
            </div>
            <div className="value-card" style={{ animationDelay: `0.1s` }}>
              <div className="value-icon-svg"><FaUserTie /></div>
              <h3>{t('Професионализъм', language)}</h3>
              <p>{t('Работим с уважение, отговорност и отдаденост към всеки клиент.', language)}</p>
            </div>
            <div className="value-card" style={{ animationDelay: `0.2s` }}>
              <div className="value-icon-svg"><FaHandsHelping /></div>
              <h3>{t('Грижа', language)}</h3>
              <p>{t('За нас здравето и доверието са най-ценните ресурси.', language)}</p>
            </div>
            <div className="value-card" style={{ animationDelay: `0.3s` }}>
              <div className="value-icon-svg"><FaLock /></div>
              <h3>{t('Доверие', language)}</h3>
              <p>{t('Изграждаме дългосрочни отношения, основани на честност и прозрачност.', language)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="about-testimonials" ref={testimonialsRef}>
        <div className="about-container">
          <h3 className="section-label">{t('ОТЗИВИ', language)}</h3>
          <h2>{t(bgContent.testimonials.title, language)}</h2>

          <div className="testimonial-icons-row">
            {testimonials.map((testimonial, idx) => (
              <button
                key={idx}
                className={`testimonial-icon-btn${selectedReview === idx ? ' active' : ''}`}
                onClick={() => setSelectedReview(idx)}
                aria-label={testimonial.name}
              >
                <span className="testimonial-icon-emoji">
                  {testimonial.gender === 'man' ? <MdPerson /> : <MdWoman />}
                </span>
              </button>
            ))}
          </div>

          <div className="testimonial-dots-row">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`testimonial-dot${selectedReview === idx ? ' active' : ''}`}
                onClick={() => setSelectedReview(idx)}
                aria-label={`Select review ${idx + 1}`}
              />
            ))}
          </div>

          <div className="testimonial-main-quote">
            <span className="testimonial-quote-mark">“</span>
            <span className="testimonial-quote-text">
              {language === 'bg'
                ? testimonials[selectedReview].feedback_bg
                : testimonials[selectedReview].feedback_en}
            </span>
            <span className="testimonial-quote-mark">”</span>
          </div>
          <div className="testimonial-main-author">
            <strong>{t(testimonials[selectedReview].name, language)}</strong>
            <div className="testimonial-main-role">{t(testimonials[selectedReview].role, language)}</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta" ref={ctaRef}>
        <div className="cta-background">
          <div className="floating-particle particle-1">🧬</div>
          <div className="floating-particle particle-2">💎</div>
          <div className="floating-particle particle-3">✨</div>
        </div>
        <div className="about-container">
          <div className="cta-content">
            <h2>{t(bgContent.cta.title, language)}</h2>
            <p>{t(bgContent.cta.description, language)}</p>
            <Link to="/contact" className="cta-button">
              <span>{t(bgContent.cta.button, language)}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 